import { useState, useEffect } from 'react';
import { cn } from '@anytag/shared-utils';
import { SelectOption } from '@anytag/shared-types';
import { manageLocalStorage, LocalStorageKey } from '@anytag/router';
import { Button, Select, ButtonProps, SelectOptionValue, MultipleSelectProps } from '@anytag/shared-atoms';

export interface ColumnCustomiserProps<TOption extends SelectOption<string>>
  extends Pick<MultipleSelectProps<TOption>, 'className' | 'onChange' | 'dropdownAlignment' | 'options'> {
  buttonProps?: ButtonProps;
  savedValuesKey?: LocalStorageKey; // If Not put, the selection will be clear on sort/refresh
}

export const ColumnCustomiser = <TOption extends SelectOption<string>>({
  options,
  onChange,
  className,
  buttonProps,
  savedValuesKey,
  dropdownAlignment
}: ColumnCustomiserProps<TOption>) => {
  const [selectedValues, setSelectedValues] = useState<SelectOptionValue<TOption>[]>([]);

  useEffect(() => {
    if (savedValuesKey) {
      const savedColumnsState = manageLocalStorage('get', savedValuesKey);
      if (savedColumnsState) {
        const savedValue = savedColumnsState.split(', ') as SelectOptionValue<TOption>[];
        setSelectedValues(savedValue);
        setTimeout(() => {
          onChange?.(savedValue);
        }, 0); // wait for the table ready
      }
    }
    return () => {
      if (savedValuesKey === 'adReportTableColumns') {
        manageLocalStorage('remove', savedValuesKey);
      }
    };
  }, []);

  const handleChange = (value: SelectOptionValue<TOption>[]) => {
    setSelectedValues(value);
    onChange?.(value);
    if (savedValuesKey) {
      manageLocalStorage('set', savedValuesKey, value.join(', '));
    }
  };

  return (
    <Select
      options={options}
      value={selectedValues}
      onChange={handleChange}
      classNames={{ dropdown: 'min-w-75', menu: 'max-h-75' }}
      dropdownAlignment={{ align: 'start', side: 'right', ...dropdownAlignment }}
      triggerNode={
        <Button
          variant="outline"
          className={cn(className, 'px-1.5')}
          prefixIcon={{ icon: 'icon-filter', size: 18, variant: 'gray' }}
          {...buttonProps}
        />
      }
      multiple
    />
  );
};
